// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useCallback, useMemo } from 'react';
import { Alert, AppIcon, FormInput, Button, Checkbox, Spinner, Dropdown, TextArea } from '@loa/ui-components';
import { usePolicyMetadata } from '../hooks/usePolicyMetadata';
import type { SaveStructuredDetailsRequest } from '../types/policyMetadata';
import { METADATA_ACCORDION_SECTIONS, FIELD_METADATA } from '../types/policyMetadata';
import { mergeMetadataFields } from '../utils/metadataFieldMerge';
import { validateMetadataField, isSectionComplete } from '../utils/policyMetadataValidation';
import { PhoneFlagDropdown, DEFAULT_PHONE_COUNTRY, phoneCountries } from './PhoneFlagDropdown';
import { PolicyMetadataChangeReasonModal } from './PolicyMetadataChangeReasonModal';
import { usePolicyTypeOptions } from '../hooks/usePolicyTypeOptions';
import { FileUploadField } from './providerAccess/shared/FileUploadField';
import { providerSubmissionApi } from '../api/providerSubmissionApi';
import type { ProviderSubmissionDTO } from '../types/providerAccess';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import { SUPPORTED_COUNTRIES, DEFAULT_COUNTRY, getCountryConfig, normalizePostalCode } from '../config/addressConfig';
import './PolicyMetadataTab.css';

interface ChangeReasonModalState {
  open: boolean;
  sectionKey: string;
  sectionLabel: string;
}

interface PolicyMetadataTabProps {
  policyId: string;
  policyNumber: string;
  policyCategory?: string;
  providerId?: string;
  brokerageId?: string;
}

/** Coerce a metadata field value to a string. Objects/arrays become '' so
 *  display code never produces '[object Object]'. */
function asScalarString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** Format a numeric string for display in a currency field. Strips leading
 *  zeros and adds thousand-separator commas. Empty input returns empty. */
function formatCurrencyDisplay(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = (parts[0] || '0').replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

/** Strip non-digit / non-decimal characters from a display string and
 *  collapse multiple decimals to a single one. */
function toRawNumber(display: string): string {
  const stripped = display.replaceAll(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

/** Round a numeric string to two decimal places for euro-amount on-blur
 *  formatting. Returns empty for non-numeric input. */
function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = Number.parseFloat(raw);
  if (Number.isNaN(num)) return '';
  return num.toFixed(2);
}

/** Initial section form data assembled from the merged metadata field map.
 *  Pure helper extracted from toggleSection so that branch's cognitive
 *  complexity stays under the SonarQube limit. */
function buildSectionDataFromMerged(
  fields: ReadonlyArray<string>,
  mergedFields: Record<string, { value?: unknown }>,
): Record<string, unknown> {
  const sectionData: Record<string, unknown> = {};
  for (const fieldName of fields) {
    const merged = mergedFields[fieldName];
    if (merged?.value !== undefined && merged.value !== null) {
      sectionData[fieldName] = merged.value;
    }
  }
  if (fields.includes('customerCountry') && !sectionData.customerCountry) {
    sectionData.customerCountry = DEFAULT_COUNTRY;
  }
  return sectionData;
}

/** Infer a phone-input country code from an existing E.164-ish value.
 *  Returns null when nothing useful can be derived — caller keeps default. */
function inferPhoneCountryCode(phone: string): string | null {
  try {
    const parsed = parsePhoneNumber(phone);
    if (parsed?.country) return parsed.country;
    const byLongestDialCode = [...phoneCountries].sort(
      (a, b) => getCountryCallingCode(b).length - getCountryCallingCode(a).length,
    );
    for (const cc of byLongestDialCode) {
      if (phone.startsWith(`+${getCountryCallingCode(cc)}`)) return cc;
    }
  } catch {
    /* fall through */
  }
  return null;
}


/** Sections whose content is rendered with a custom wizard-mirroring layout
 *  (rather than the default field-by-field renderer). Member of this set →
 *  use one of the renderXxxContent helpers; non-member → fallback. */
const WIZARD_LAYOUT_SECTION_KEYS: ReadonlySet<string> = new Set([
  'policyDetails',
  'crystallisedRetired',
  'investments',
  'investmentFunds',
  'investmentUploads',
  'chargesFees',
  'additionalInfo',
  'investmentOtherInfo',
  'transfersIn',
  'transactionHistory',
  'otherBenefits',
]);

// Contribution detail fields hidden when policy is Paid Up or Deferred
// (only show totals: totalEmployerContributions, totalEmployeeContributions, totalTransfersIn, payingStatus)
const CONTRIBUTION_DETAIL_FIELDS = new Set([
  'employerContribution', 'employeeContribution',
  'regularPremiumAmount', 'regularPremiumFrequency',
  'singlePremiumAmount', 'singlePremiumFrequency',
  'dateOfLastContribution', 'indexationDetails', 'policyPaidBy',
  'dueDateNextPayment', 'dueAmountNextPayment', 'transfersInFromAnotherScheme'
]);

/** Sections to hide based on policy category */
function getHiddenSections(policyCategory: string): Set<string> {
  const hidden = new Set<string>();
  const cat = policyCategory.toUpperCase();

  // INVESTMENT: show only investment-specific sections + shared policyholder/investments
  if (cat === 'INVESTMENT') {
    hidden.add('providerInfo');
    hidden.add('policyDetails');
    hidden.add('employmentDetails');
    hidden.add('contributionDetails');
    hidden.add('crystallisedRetired');
    hidden.add('investments');
    hidden.add('chargesFees');
    hidden.add('transfersIn');
    hidden.add('transactionHistory');
    hidden.add('otherBenefits');
    hidden.add('additionalInfo');
  }

  // Non-INVESTMENT: hide investment-only sections
  if (cat !== 'INVESTMENT') {
    hidden.add('investmentProviderInfo');
    hidden.add('investmentPolicyDetails');
    hidden.add('investmentFunds');
    hidden.add('investmentOtherInfo');
    hidden.add('investmentUploads');
    hidden.add('investmentChargesFees');
    hidden.add('investmentOtherCharges');
  }

  // ARF / Vested PRSA: no contributions (they have withdrawals), no protection benefits
  if (cat.includes('ARF') || cat.includes('VESTED PRSA')) {
    hidden.add('contributionDetails');
    hidden.add('otherBenefits');
  }

  // PRB: no contributions (ring-fenced, cannot accept contributions)
  if (cat.includes('PRB') || cat.includes('PERSONAL RETIREMENT BOND')) {
    hidden.add('contributionDetails');
  }

  return hidden;
}

export const PolicyMetadataTab: React.FC<PolicyMetadataTabProps> = ({
  policyId,
  policyNumber,
  policyCategory = '',
  providerId = '',
  brokerageId = '',
}) => {
  const { categoryOptions: providerCategoryOptions } = usePolicyTypeOptions(brokerageId, providerId);
  const {
    structuredDetails,
    latestProviderSubmission,
    providerDocuments,
    loading,
    saving,
    error,
    saveStructuredDetails,
    clearError
  } = usePolicyMetadata(policyId);

  // Per-section form state
  const [sectionFormData, setSectionFormData] = useState<Record<string, Record<string, unknown>>>({});
  const [dirtySections, setDirtySections] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [sectionErrors, setSectionErrors] = useState<Record<string, Record<string, string>>>({});
  const [phoneCountryCode, setPhoneCountryCode] = useState(DEFAULT_PHONE_COUNTRY);
  const [changeReasonModal, setChangeReasonModal] = useState<ChangeReasonModalState>({ open: false, sectionKey: '', sectionLabel: '' });
  const [pendingFiles, setPendingFiles] = useState<Map<string, File[]>>(new Map());

  // Pending-file mutators — extracted out of renderFieldInput so the inner
  // setState callbacks aren't 5 levels deep.
  const addPendingFile = useCallback((key: string, file: File) => {
    setPendingFiles(prev => {
      const next = new Map(prev);
      next.set(key, [...(next.get(key) || []), file]);
      return next;
    });
  }, []);

  const removePendingFile = useCallback((key: string, index?: number) => {
    setPendingFiles(prev => {
      const next = new Map(prev);
      if (index === undefined) {
        next.delete(key);
        return next;
      }
      const existing = next.get(key) || [];
      next.set(key, existing.filter((_, i) => i !== index));
      return next;
    });
  }, []);

  // Merged field values
  const mergedFields = useMemo(() => {
    const structuredFields = structuredDetails?.fields || {};
    const providerFormData = latestProviderSubmission?.formData as Record<string, unknown> | null;
    return mergeMetadataFields(structuredFields, providerFormData);
  }, [structuredDetails, latestProviderSubmission]);

  // Provider document map: fieldKey → latest DOCUMENT submission
  const providerDocumentMap = useMemo(() => {
    const map = new Map<string, ProviderSubmissionDTO>();
    for (const doc of providerDocuments) {
      const fieldKey = (doc.formData as Record<string, unknown>)?.fieldKey as string;
      if (fieldKey) {
        const existing = map.get(fieldKey);
        if (!existing || new Date(doc.submittedAt) > new Date(existing.submittedAt)) {
          map.set(fieldKey, doc);
        }
      }
    }
    return map;
  }, [providerDocuments]);

  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const handleDownloadProviderDoc = useCallback(async (submissionId: string, filename: string) => {
    setDownloadingDocId(submissionId);
    try {
      const blob = await providerSubmissionApi.downloadDocument(policyId, submissionId);
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } finally {
      setDownloadingDocId(null);
    }
  }, [policyId]);

  const formatFileSize = (bytes?: number | null): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // =========================================================================
  // Section Handlers
  // =========================================================================

  const validateAllSectionFields = useCallback((sectionKey: string, visibleFields: string[], sectionData: Record<string, unknown>) => {
    const errors: Record<string, string> = {};
    for (const fieldName of visibleFields) {
      const error = validateMetadataField(sectionData, fieldName);
      if (error) errors[fieldName] = error;
    }
    setSectionErrors(prev => ({ ...prev, [sectionKey]: { ...prev[sectionKey], ...errors } }));
  }, []);

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
        return next;
      }
      next.add(sectionKey);
      if (sectionFormData[sectionKey]) return next;

      const section = METADATA_ACCORDION_SECTIONS.find(s => s.key === sectionKey);
      if (!section) return next;

      const sectionData = buildSectionDataFromMerged(section.fields, mergedFields);
      setSectionFormData(prevForm => ({ ...prevForm, [sectionKey]: sectionData }));

      if (typeof sectionData.customerPhone === 'string' && sectionData.customerPhone) {
        const inferred = inferPhoneCountryCode(sectionData.customerPhone);
        if (inferred) setPhoneCountryCode(inferred);
      }
      return next;
    });
  }, [sectionFormData, mergedFields]);

  const handleFieldChange = useCallback((
    sectionKey: string,
    fieldName: string,
    value: unknown
  ) => {
    setSectionFormData(prev => {
      const updatedSection = { ...prev[sectionKey], [fieldName]: value };

      // Inline validation
      const fieldError = validateMetadataField(updatedSection, fieldName);
      const errorUpdates: Record<string, string> = { [fieldName]: fieldError };

      // Re-validate postal code when country changes
      if (fieldName === 'customerCountry') {
        errorUpdates.customerPostalCode = validateMetadataField(updatedSection, 'customerPostalCode');
      }

      setSectionErrors(prevErrors => ({
        ...prevErrors,
        [sectionKey]: { ...prevErrors[sectionKey], ...errorUpdates }
      }));

      return { ...prev, [sectionKey]: updatedSection };
    });
    setDirtySections(prev => new Set(prev).add(sectionKey));
  }, []);

  const openChangeReasonModal = useCallback((sectionKey: string) => {
    const section = METADATA_ACCORDION_SECTIONS.find(s => s.key === sectionKey);
    setChangeReasonModal({ open: true, sectionKey, sectionLabel: section?.label || sectionKey });
  }, []);

  const handleSaveSectionWithReason = useCallback(async (changeReason: string, asOfDateTime: string) => {
    const sectionKey = changeReasonModal.sectionKey;
    const sectionData = sectionFormData[sectionKey] || {};

    // Only merge existing structured details with current section edits
    // Do not include mergedFields (provider-sourced values) — only user edits should generate log entries
    const existingFields = structuredDetails?.fields || {};
    const updatedFields = {
      ...existingFields,
      ...sectionData
    };

    // Clean up empty string values
    for (const [key, val] of Object.entries(updatedFields)) {
      if (val === '' || val === undefined) {
        delete updatedFields[key];
      }
    }

    const request: SaveStructuredDetailsRequest = {
      changeReason: asOfDateTime ? `${changeReason} [As of: ${asOfDateTime}]` : changeReason,
      fields: updatedFields
    };

    await saveStructuredDetails(request);
    setDirtySections(prev => {
      const next = new Set(prev);
      next.delete(sectionKey);
      return next;
    });
  }, [changeReasonModal.sectionKey, sectionFormData, structuredDetails, saveStructuredDetails]);

  // =========================================================================
  // Field Renderers
  // =========================================================================

  // Resolve country code from section data for dynamic postal code labels
  const resolveCountryCodeFromSection = useCallback((sectionKey: string): string => {
    const data = sectionFormData[sectionKey] || {};
    const country = data.customerCountry;
    if (!country || typeof country !== 'string') return DEFAULT_COUNTRY;
    // Check if it's already a supported code
    if (SUPPORTED_COUNTRIES.some(c => c.value === country)) return country;
    // Map names to codes
    const lower = country.toLowerCase();
    if (lower === 'ireland') return 'IRL';
    if (lower === 'united kingdom' || lower === 'uk') return 'GBR';
    return DEFAULT_COUNTRY;
  }, [sectionFormData]);

  /** Check if a field should be visible based on its visibleWhen condition */
  const isFieldVisible = useCallback((fieldName: string, sectionData: Record<string, unknown>): boolean => {
    const metadata = FIELD_METADATA[fieldName];
    if (!metadata?.visibleWhen) return true;
    const condition = metadata.visibleWhen;

    // Support "field=VALUE" syntax for specific value matching
    if (condition.includes('=')) {
      const [parentField, expectedValue] = condition.split('=');
      return asScalarString(sectionData[parentField]) === expectedValue;
    }

    // Default: boolean true or any truthy non-empty select value
    const parentValue = sectionData[condition];
    if (parentValue === true) return true;
    if (typeof parentValue === 'string' && parentValue !== '' && parentValue !== 'NO') return true;
    return false;
  }, []);

  // Sections hidden based on policy category
  const hiddenSections = useMemo(() => getHiddenSections(policyCategory), [policyCategory]);

  /** Check if a contribution detail field should be hidden based on policy status */
  const isContributionFieldVisible = useCallback((sectionKey: string, fieldName: string): boolean => {
    if (sectionKey !== 'contributionDetails' || !CONTRIBUTION_DETAIL_FIELDS.has(fieldName)) return true;
    // Check policyStatus from the policyDetails section data
    const policyDetailsData = sectionFormData['policyDetails'] || {};
    const status = policyDetailsData.policyStatus as string || '';
    // Hide detailed contribution fields for Paid Up / Deferred
    return status !== 'PAID_UP' && status !== 'DEFERRED';
  }, [sectionFormData]);

  const renderFieldInput = (sectionKey: string, fieldName: string, value: unknown, fieldError?: string, placeholderOverride?: string) => {
    const metadata = FIELD_METADATA[fieldName];
    if (!metadata) return null;

    const stringValue = value === null || value === undefined ? '' : String(value);
    const placeholder = placeholderOverride || metadata.placeholder;

    switch (metadata.type) {
      case 'textarea':
        return (
          <TextArea
            placeholder={placeholder}
            value={stringValue}
            onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value)}
            error={fieldError}
            fluid
          />
        );

      case 'number':
        return (
          <FormInput
            type="number"
            step="0.01"
            placeholder={placeholder}
            value={stringValue}
            onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value ? Number.parseFloat(e.target.value) : null)}
            error={fieldError}
          />
        );

      case 'currency': {
        const rawVal = stringValue;
        return (
          <>
            <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${
              fieldError ? 'border-[#E7000B]' : 'border-grey-04 focus-within:border-purple-primary'
            } bg-white transition-colors`}>
              <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder={placeholder}
                value={formatCurrencyDisplay(rawVal)}
                onChange={(e) => {
                  const stripped = e.target.value.replaceAll(/[^0-9.]/g, '');
                  const parts = stripped.split('.');
                  const cleaned = parts.length <= 1 ? stripped : parts[0] + '.' + parts.slice(1).join('');
                  if (cleaned && !/^\d*\.?\d*$/.test(cleaned)) return;
                  handleFieldChange(sectionKey, fieldName, cleaned);
                }}
                onBlur={() => {
                  if (rawVal) {
                    const num = Number.parseFloat(rawVal);
                    if (!Number.isNaN(num)) {
                      handleFieldChange(sectionKey, fieldName, num.toFixed(2));
                    }
                  }
                }}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
            </div>
            {fieldError && <span className="block text-body-04 text-[#E7000B] mt-xs">{fieldError}</span>}
          </>
        );
      }

      case 'percent': {
        const percentVal = stringValue;
        return (
          <>
            <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${
              fieldError ? 'border-[#E7000B]' : 'border-grey-04 focus-within:border-purple-primary'
            } bg-white transition-colors`}>
              <input
                type="text"
                inputMode="decimal"
                placeholder={placeholder}
                value={percentVal}
                onChange={(e) => {
                  const stripped = e.target.value.replaceAll(/[^0-9.]/g, '');
                  const parts = stripped.split('.');
                  const cleaned = parts.length <= 1 ? stripped : parts[0] + '.' + parts.slice(1).join('');
                  if (cleaned && !/^\d*\.?\d*$/.test(cleaned)) return;
                  handleFieldChange(sectionKey, fieldName, cleaned);
                }}
                className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
              />
              <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none" style={{ marginLeft: '8px' }}>%</span>
            </div>
            {fieldError && <span className="block text-body-04 text-[#E7000B] mt-xs">{fieldError}</span>}
          </>
        );
      }

      case 'date': {
        const calendarSvg = (
          <AppIcon
            name="calendar field"
            size={16}
            color="#141414"
            className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none"
          />
        );
        return (
          <>
            <div className="relative">
              <input
                type={stringValue ? 'date' : 'text'}
                placeholder="DD/MM/YYYY"
                value={stringValue}
                onFocus={(e) => { e.currentTarget.type = 'date'; }}
                onBlur={(e) => { if (!e.currentTarget.value) e.currentTarget.type = 'text'; }}
                onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value || null)}
                className={`w-full h-[48px] px-md pr-2xl rounded-lg border ${
                  fieldError ? 'border-[#E7000B]' : 'border-grey-04'
                } bg-white ${stringValue ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
              />
              {calendarSvg}
            </div>
            {fieldError && <span className="block text-[#E7000B] font-sans text-body-03 font-normal leading-normal mt-xs">{fieldError}</span>}
          </>
        );
      }

      case 'email':
        return (
          <FormInput
            type="email"
            placeholder={placeholder}
            value={stringValue}
            onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value)}
            error={fieldError}
          />
        );

      case 'select': {
        const selectOptions = (metadata.options || [])
          .filter(opt => opt.value !== '')
          .map(opt => ({ value: opt.value, text: opt.label }));
        const placeholderOpt = metadata.options?.find(opt => opt.value === '');
        return (
          <Dropdown
            placeholder={placeholderOpt?.label || 'Select...'}
            options={selectOptions}
            value={stringValue}
            onChange={(value) => handleFieldChange(sectionKey, fieldName, value)}
            error={fieldError}
          />
        );
      }

      case 'boolean':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <span className="sr-only">{metadata.label}</span>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(value)}
              onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.checked)}
              aria-label={metadata.label}
            />
            <div className="w-[44px] h-[24px] bg-[#e1e1e1] peer-focus:outline-none rounded-full peer peer-checked:bg-purple-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-[20px] after:w-[20px] after:transition-all peer-checked:after:translate-x-[20px]" />
          </label>
        );

      case 'phone':
        return (
          <>
            <div className="flex gap-sm">
              <PhoneFlagDropdown
                value={phoneCountryCode}
                onSelect={(alpha2, dialCode) => {
                  setPhoneCountryCode(alpha2);
                  handleFieldChange(sectionKey, fieldName, `+${dialCode} `);
                }}
              />
              <FormInput
                type="tel"
                placeholder={placeholder}
                value={stringValue}
                onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value)}
                className="flex-1"
              />
            </div>
            {fieldError && <span className="block text-body-04 text-[#E7000B] mt-xs">{fieldError}</span>}
          </>
        );

      case 'country': {
        return (
          <Dropdown
            options={SUPPORTED_COUNTRIES.map(c => ({ value: c.value, text: c.text }))}
            value={stringValue || DEFAULT_COUNTRY}
            onChange={(code) => {
              handleFieldChange(sectionKey, fieldName, code);
              // Normalize existing postal code for the new country
              const currentPostal = sectionFormData[sectionKey]?.customerPostalCode;
              if (typeof currentPostal === 'string' && currentPostal) {
                handleFieldChange(sectionKey, 'customerPostalCode', normalizePostalCode(code, currentPostal));
              }
            }}
            error={fieldError}
          />
        );
      }

      case 'file': {
        const savedFileName = typeof value === 'string' && value ? value : null;
        const hasPendingFiles = (pendingFiles.get(fieldName) || []).length > 0;

        // Show saved file indicator if provider already uploaded
        if (savedFileName && !hasPendingFiles) {
          return (
            <div className="flex flex-col gap-sm">
              <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                {metadata.label}
              </div>
              <div className="flex items-center justify-between px-lg py-md rounded-lg border border-[#A3A3A3] bg-white">
                <div className="flex items-center gap-sm">
                  <AppIcon name="file document" size={32} color="#004E24" />
                  <div className="text-black font-sans text-body-03 font-medium leading-normal">
                    {savedFileName}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // Normal upload field
        return (
          <FileUploadField
            fieldKey={fieldName}
            label={metadata.label}
            files={pendingFiles.get(fieldName)}
            onAddFile={(key, file) => {
              addPendingFile(key, file);
              handleFieldChange(sectionKey, fieldName, file.name);
            }}
            onRemoveFile={(key, index) => {
              removePendingFile(key, index);
              const remaining = (pendingFiles.get(fieldName) || []).length;
              if (remaining <= 1) handleFieldChange(sectionKey, fieldName, null);
            }}
          />
        );
      }

      case 'postalCode': {
        const countryCode = resolveCountryCodeFromSection(sectionKey);
        const countryConfig = getCountryConfig(countryCode);
        return (
          <FormInput
            placeholder={countryConfig.postalCodePlaceholder}
            value={stringValue}
            onChange={(e) => {
              const normalized = normalizePostalCode(countryCode, e.target.value);
              handleFieldChange(sectionKey, fieldName, normalized);
            }}
            error={fieldError}
          />
        );
      }

      default:
        return (
          <FormInput
            placeholder={placeholder}
            value={stringValue}
            onChange={(e) => handleFieldChange(sectionKey, fieldName, e.target.value)}
            error={fieldError}
          />
        );
    }
  };

  // =========================================================================
  // =========================================================================
  // Other Associated Benefits — Custom Section Render (mirrors StepAssociatedBenefits wizard)
  // =========================================================================

  const LIFE_COVER_TYPES = [
    { value: 'Death in Service', text: 'Death in Service' },
    { value: 'Executive Term Cover', text: 'Executive Term Cover' },
    { value: 'Term Life Insurance', text: 'Term Life Insurance' },
    { value: 'Whole of Life', text: 'Whole of Life' },
    { value: 'Other', text: 'Other' },
  ];

  const INCOME_PROTECTION_TYPES = [
    { value: 'Permanent Health Insurance', text: 'Permanent Health Insurance' },
    { value: 'Executive Income Protection', text: 'Executive Income Protection' },
    { value: 'Group Income Protection', text: 'Group Income Protection' },
    { value: 'Other', text: 'Other' },
  ];

  const SPECIFIED_ILLNESS_TYPES = [
    { value: 'Specified Illness Cover', text: 'Specified Illness Cover' },
    { value: 'Critical Illness Cover', text: 'Critical Illness Cover' },
    { value: 'Other', text: 'Other' },
  ];

  const YES_NO_BENEFIT_OPTIONS = [
    { value: 'Yes', text: 'Yes' },
    { value: 'No', text: 'No' },
  ];

  type BenefitCardEntry = Record<string, string | undefined>;

  const BENEFIT_CONFIG: { key: string; enabledKey: string; cardsKey: string; label: string; typeOptions: { value: string; text: string }[]; salaryLabel: string; secondFieldLabel: string }[] = [
    { key: 'lifeCover', enabledKey: 'lifeCoverEnabled', cardsKey: 'lifeCoverCards', label: 'Life Cover', typeOptions: LIFE_COVER_TYPES, salaryLabel: 'Salary on record', secondFieldLabel: 'Times salary' },
    { key: 'incomeProtection', enabledKey: 'incomeProtectionEnabled', cardsKey: 'incomeProtectionCards', label: 'Income Protection', typeOptions: INCOME_PROTECTION_TYPES, salaryLabel: 'Salary on record for cover', secondFieldLabel: '% of salary' },
    { key: 'specifiedIllness', enabledKey: 'specifiedIllnessEnabled', cardsKey: 'specifiedIllnessCards', label: 'Specified Illness', typeOptions: SPECIFIED_ILLNESS_TYPES, salaryLabel: 'Salary on record', secondFieldLabel: 'Times salary' },
  ];

  // ── Policy Details custom renderer (matches StepPolicyDetails + StepPolicyStatus design) ──
  const renderPolicyDetailsContent = (
    fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';

    const status = asScalarString(fullSectionData.policyStatus);
    const isClosed = status === 'CLOSED';
    const closedReason = asScalarString(fullSectionData.closedReason);
    const isRetired = isClosed && closedReason === 'RETIRED';
    const isTransferred = isClosed && closedReason === 'TRANSFERRED_OUT';
    let closedSuffix = '';
    if (isRetired) closedSuffix = ' (retired to)';
    else if (isTransferred) closedSuffix = ' (transferred to)';

    // Determine options for closed policy type based on closed reason
    const closedPolicyTypeOptions = isRetired
      ? [
          { value: '', text: 'Choose an option' },
          { value: 'ARF', text: 'ARF' },
          { value: 'VESTED_PRSA', text: 'Vested PRSA' },
          { value: 'OVERSEAS_TRANSFER', text: 'Overseas Transfer' },
        ]
      : [
          { value: '', text: 'Choose an option' },
          { value: 'PRB_BOB', text: 'PRB / Buy-out Bond' },
          { value: 'PRSA_STANDARD', text: 'PRSA (Standard)' },
          { value: 'PRSA_NON_STANDARD', text: 'PRSA (Non-Standard)' },
          { value: 'PRSA_AVC', text: 'PRSA AVC' },
          { value: 'PERSONAL_PENSION', text: 'Personal Pension' },
          { value: 'DEFINED_CONTRIBUTION', text: 'Defined Contribution' },
          { value: 'EXECUTIVE_PENSION', text: 'Executive Pension' },
          { value: 'MASTERTRUST', text: 'Mastertrust' },
          { value: 'DIRECTORS_PENSION', text: "Director's Pension" },
          { value: 'ARF', text: 'ARF (Approved Retirement Fund)' },
          { value: 'VESTED_PRSA', text: 'Vested PRSA' },
          { value: 'OVERSEAS_TRANSFER', text: 'Overseas Transfer' },
        ];

    // Default typeOfPolicy from policyCategory prop
    const typeOfPolicyVal = (asScalarString(getVal('typeOfPolicy')) || policyCategory || '').toUpperCase();

    const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";
    const inputClass = `w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal`;
    const dateInputClass = (hasValue: boolean) =>
      `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

    const calendarIcon = (
      <AppIcon
        name="calendar field"
        size={16}
        color="#141414"
        className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none"
      />
    );

    const renderSelect = (field: string, label: string, options: { value: string; text: string }[], required = true) => {
      const val = asScalarString(getVal(field));
      const firstOpt = options[0];
      const placeholder = firstOpt?.value === '' ? firstOpt.text : 'Choose an option';
      const filteredOptions = options.filter(o => o.value !== '');
      return (
        <Dropdown
          label={label}
          required={required}
          placeholder={placeholder}
          options={filteredOptions}
          value={val}
          onChange={(value) => handleFieldChange(sectionKey, field, value)}
          error={getErr(field)}
        />
      );
    };

    const renderText = (field: string, label: string, required = true, placeholder?: string) => {
      const val = asScalarString(getVal(field));
      return (
        <div className="flex flex-col gap-sm">
          <div className={labelClass}>{label}{required && <span className="text-[#E7000B]"> *</span>}</div>
          <input
            type="text"
            value={val}
            placeholder={placeholder}
            onChange={(e) => handleFieldChange(sectionKey, field, e.target.value)}
            className={inputClass}
          />
          {getErr(field) && <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getErr(field)}</div>}
        </div>
      );
    };

    const renderDate = (field: string, label: string, required = true) => {
      const val = asScalarString(getVal(field));
      return (
        <div className="flex flex-col gap-sm">
          <div className={labelClass}>{label}{required && <span className="text-[#E7000B]"> *</span>}</div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={val}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => { if (!e.currentTarget.value) e.currentTarget.type = 'text'; }}
              onChange={(e) => handleFieldChange(sectionKey, field, e.target.value)}
              className={dateInputClass(!!val)}
            />
            {calendarIcon}
          </div>
          {getErr(field) && <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getErr(field)}</div>}
        </div>
      );
    };

    const renderCurrency = (field: string, label: string, required = true) => {
      const raw = asScalarString(getVal(field));
      return (
        <div className="flex flex-col gap-sm">
          <div className={labelClass}>{label}{required && <span className="text-[#E7000B]"> *</span>}</div>
          <div className="flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary">
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none mr-sm">€</span>
            <input
              type="text"
              inputMode="decimal"
              value={raw ? formatCurrencyDisplay(raw) : ''}
              onChange={(e) => {
                const r = toRawNumber(e.target.value);
                if (r && !/^\d*\.?\d*$/.test(r)) return;
                handleFieldChange(sectionKey, field, r);
              }}
              onBlur={() => {
                if (raw) handleFieldChange(sectionKey, field, formatEuroBlur(raw));
              }}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getErr(field) && <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getErr(field)}</div>}
        </div>
      );
    };

    const pensionTypeOptions = [
      { value: '', text: 'Select pension type...' },
      { value: 'PRB_BOB', text: 'PRB / Buy-out Bond' },
      { value: 'PRSA_STANDARD', text: 'PRSA (Standard)' },
      { value: 'PRSA_NON_STANDARD', text: 'PRSA (Non-Standard)' },
      { value: 'PRSA_AVC', text: 'PRSA AVC' },
      { value: 'PERSONAL_PENSION', text: 'Personal Pension' },
      { value: 'DEFINED_CONTRIBUTION', text: 'Defined Contribution' },
      { value: 'EXECUTIVE_PENSION', text: 'Executive Pension' },
      { value: 'MASTERTRUST', text: 'Mastertrust' },
      { value: 'DIRECTORS_PENSION', text: "Director's Pension" },
      { value: 'ARF', text: 'ARF (Approved Retirement Fund)' },
      { value: 'VESTED_PRSA', text: 'Vested PRSA' },
    ];

    const typeOfPolicyOptions = [
      { value: '', text: 'Select type...' },
      ...providerCategoryOptions,
    ];

    const statusOptions = [
      { value: '', text: 'Select status...' },
      { value: 'ACTIVE', text: 'Active' },
      { value: 'CLOSED', text: 'Closed' },
    ];

    const closedReasonOptions = [
      { value: '', text: 'Choose an option...' },
      { value: 'RETIRED', text: 'Retired' },
      { value: 'TRANSFERRED_OUT', text: 'Transferred out' },
    ];

    const transferTypeOptions = [
      { value: '', text: 'Choose an option' },
      { value: 'CASH', text: 'Cash' },
      { value: 'IN_SPECIE', text: 'In-specie' },
    ];

    return (
      <div className="flex flex-col gap-lg">
        {/* Row 1: 3 columns — Policy number, Type of policy, Pension Type */}
        <div className="grid grid-cols-3 gap-lg max-md:grid-cols-1 items-end">
          {renderText('policyAccountNumber', 'Policy number / Plan Reference No / Member Reference No')}
          {(() => {
            // typeOfPolicy: pre-populate from policyCategory prop if no saved value
            const val = typeOfPolicyVal;
            const placeholder = typeOfPolicyOptions[0]?.value === '' ? typeOfPolicyOptions[0].text : 'Choose an option';
            const filteredTypeOfPolicyOptions = typeOfPolicyOptions.filter(o => o.value !== '');
            return (
              <Dropdown
                label="Type of policy"
                required
                placeholder={placeholder}
                options={filteredTypeOfPolicyOptions}
                value={val}
                onChange={(value) => handleFieldChange(sectionKey, 'typeOfPolicy', value)}
                error={getErr('typeOfPolicy')}
              />
            );
          })()}
          {renderSelect('pensionType', 'Pension Type', pensionTypeOptions)}
        </div>

        {/* Policy status — full width */}
        {renderSelect('policyStatus', 'What is the policy status?', statusOptions)}

        {/* Closed reason — full width (only when CLOSED) */}
        {isClosed && renderSelect('closedReason', 'Closed status', closedReasonOptions)}

        {/* Closed sub-fields: Retired or Transferred */}
        {(isRetired || isTransferred) && (
          <>
            {/* Provider name + Date of Transfer */}
            <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
              {renderText('closedProviderName', `Provider name${closedSuffix}`)}
              {renderDate('closedDateOfTransfer', 'Date of Transfer')}
            </div>

            {/* Policy reference + Type of Policy */}
            <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
              {renderText('closedPolicyReference', `Policy reference number${closedSuffix}`)}
              {renderSelect('closedPolicyType', `Type of Policy${closedSuffix}`, closedPolicyTypeOptions)}
            </div>

            {/* Type of Transfer + Value at transfer date */}
            <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
              {renderSelect('closedTransferType', 'Type of Transfer', transferTypeOptions)}
              {renderCurrency('closedTransferValue', 'Value as at transfer date')}
            </div>

            {/* Retired-only fields */}
            {isRetired && (
              <>
                <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
                  {renderDate('retiredBceDate', 'Date of Benefit Crystallisation Event')}
                  {renderCurrency('retiredBceValue', 'Value of Policy as a BCE')}
                </div>
                <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
                  {renderCurrency('retiredTaxFreeLumpSum', 'Tax Free Lump Sum')}
                  {renderText('retiredPreviousTaxFreeLumpSums', 'Details of other lump sums on record (€, dates, tax paid and connected policy)', false)}
                </div>
              </>
            )}

            {/* Transferred-only fields */}
            {isTransferred && (
              <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
                {renderText('closedTotalEmployerContributions', 'Total Employer contributions')}
                {renderText('closedTotalEmployeeContributions', 'Total Employee Contributions')}
              </div>
            )}
          </>
        )}

        {/* Current value + Date */}
        <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
          {renderCurrency('policyValue', 'Current value')}
          {renderDate('currentValueDate', 'Date as at current value')}
        </div>

        {/* Transfer value + Date */}
        <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
          {renderCurrency('transferValue', 'Transfer value')}
          {renderDate('transferValueDate', 'Date as at transfer value')}
        </div>

        {/* Scheme Revenue Reference Number */}
        <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1 items-end">
          <div className="flex flex-col gap-sm">
            <div className={labelClass}>Scheme Revenue Reference Number</div>
            <input
              type="text"
              value={asScalarString(getVal('schemeRevenueRefNumber'))}
              onChange={(e) => handleFieldChange(sectionKey, 'schemeRevenueRefNumber', e.target.value)}
              className={inputClass}
            />
            <div className="text-[#1E1E1E] font-sans text-body-03 font-normal leading-normal">
              eg. SF123456 or sometimes PB123456
            </div>
          </div>
          <div />
        </div>
      </div>
    );
  };

  const renderAssociatedBenefitsContent = (
    fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const hasProtection = asScalarString(fullSectionData.hasProtectionBenefits) === 'Yes';
    const lifeCoverEnabled = !!fullSectionData.lifeCoverEnabled;
    const incomeProtectionEnabled = !!fullSectionData.incomeProtectionEnabled;
    const specifiedIllnessEnabled = !!fullSectionData.specifiedIllnessEnabled;

    // ── Local helpers ──
    const getCards = (cardsKey: string): BenefitCardEntry[] => {
      return (fullSectionData[cardsKey] as BenefitCardEntry[] | undefined) || [];
    };

    const updateCard = (cardsKey: string, index: number, field: string, value: string) => {
      const cards = [...getCards(cardsKey)];
      cards[index] = { ...cards[index], [field]: value };
      handleFieldChange(sectionKey, cardsKey, cards);
    };

    const addCard = (cardsKey: string) => {
      handleFieldChange(sectionKey, cardsKey, [...getCards(cardsKey), {}]);
    };

    const removeCard = (cardsKey: string, index: number) => {
      handleFieldChange(sectionKey, cardsKey, getCards(cardsKey).filter((_, i) => i !== index));
    };

    const handleCheckboxToggle = (enabledKey: string, cardsKey: string, checked: boolean) => {
      handleFieldChange(sectionKey, enabledKey, checked);
      if (checked && getCards(cardsKey).length === 0) {
        handleFieldChange(sectionKey, cardsKey, [{}]);
      }
      if (!checked) {
        handleFieldChange(sectionKey, cardsKey, []);
      }
    };

    // Currency helpers are defined at module scope (formatCurrencyDisplay,
    // toRawNumber, formatEuroBlur).

    const handleCurrencyChange = (cardsKey: string, index: number, field: string, inputValue: string) => {
      const raw = toRawNumber(inputValue);
      if (raw && !/^\d*\.?\d*$/.test(raw)) return;
      updateCard(cardsKey, index, field, raw);
    };

    const handleCurrencyBlur = (cardsKey: string, index: number, field: string) => {
      const cards = getCards(cardsKey);
      const raw = (cards[index]?.[field] as string) || '';
      if (raw) updateCard(cardsKey, index, field, formatEuroBlur(raw));
    };

    // ── Style classes ──
    const abInputClass = "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";
    const abCurrencyWrapper = "flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary";
    const abLabelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";
    const abDateInputClass = (hasValue: boolean) =>
      `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

    // ── SVG icons ──
    const abCollapseIcon = (expanded: boolean) => (
      <AppIcon
        name="chevron toggle"
        size={16}
        color="#141414"
        className={`transition-transform ${expanded ? '' : 'rotate-180'}`}
      />
    );

    const abCalendarIcon = (
      <AppIcon
        name="calendar field"
        size={16}
        color="#141414"
        className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none"
      />
    );

    // ── Card field renderers ──
    const renderTextField = (cardsKey: string, idx: number, field: string, label: string, required = true) => (
      <div className="flex flex-col gap-sm flex-1">
        <div className={abLabelClass}>{label}{required && <> <span className="text-[#E7000B]">*</span></>}</div>
        <input type="text" value={getCards(cardsKey)[idx]?.[field] || ''} onChange={(e) => updateCard(cardsKey, idx, field, e.target.value)} className={abInputClass} />
      </div>
    );

    const renderCurrencyCardField = (cardsKey: string, idx: number, field: string, label: string, required = true) => (
      <div className="flex flex-col gap-sm flex-1">
        <div className={abLabelClass}>{label}{required && <> <span className="text-[#E7000B]">*</span></>}</div>
        <div className={abCurrencyWrapper}>
          <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
          <input type="text" inputMode="decimal" value={getCards(cardsKey)[idx]?.[field] ? formatCurrencyDisplay(getCards(cardsKey)[idx][field] || '') : ''} onChange={(e) => handleCurrencyChange(cardsKey, idx, field, e.target.value)} onBlur={() => handleCurrencyBlur(cardsKey, idx, field)} className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none" />
        </div>
      </div>
    );

    const renderDateCardField = (cardsKey: string, idx: number, field: string, label: string) => (
      <div className="flex flex-col gap-sm flex-1">
        <div className={abLabelClass}>{label} <span className="text-[#E7000B]">*</span></div>
        <div className="relative">
          <input type="text" placeholder="DD/MM/YYYY" value={getCards(cardsKey)[idx]?.[field] || ''} onFocus={(e) => { e.currentTarget.type = 'date'; }} onBlur={(e) => { if (!e.currentTarget.value) e.currentTarget.type = 'text'; }} onChange={(e) => updateCard(cardsKey, idx, field, e.target.value)} className={abDateInputClass(!!getCards(cardsKey)[idx]?.[field])} />
          {abCalendarIcon}
        </div>
      </div>
    );

    const renderSelectCardField = (cardsKey: string, idx: number, field: string, label: string, options: { value: string; text: string }[], required = true) => (
      <div className="flex flex-col gap-sm flex-1">
        <Dropdown
          label={label}
          required={required}
          placeholder="Choose an option"
          options={options}
          value={getCards(cardsKey)[idx]?.[field] || ''}
          onChange={(value) => updateCard(cardsKey, idx, field, value)}
        />
      </div>
    );

    // ── Specified Illness card body ──
    const renderSpecifiedIllnessBody = (index: number, cardsKey: string) => (
      <div className="flex flex-col gap-md px-lg pb-[20px]">
        <div className="flex flex-row gap-lg">
          {renderTextField(cardsKey, index, 'nameCovered', 'Name of Life Covered')}
          {renderTextField(cardsKey, index, 'policyRefNumber', 'Policy Reference Number')}
        </div>
        <div className="flex flex-row gap-lg">
          {renderCurrencyCardField(cardsKey, index, 'specifiedIllnessCover', 'Specified Illness Cover')}
          {renderTextField(cardsKey, index, 'termOfCover', 'Term of Cover')}
        </div>
        <div className="flex flex-row gap-lg">
          {renderDateCardField(cardsKey, index, 'startDateOfCover', 'Start date of cover')}
          {renderDateCardField(cardsKey, index, 'endDateOfCover', 'End date of cover')}
        </div>
        <div className="flex flex-row gap-lg">
          {renderSelectCardField(cardsKey, index, 'coverInForce', 'Cover status', [
            { value: 'In force', text: 'In force' },
            { value: 'Not active', text: 'Not active' },
          ])}
          {renderTextField(cardsKey, index, 'connectedEmployerName', 'Connected employer name', false)}
        </div>
        <div className="flex flex-row gap-lg">
          {renderTextField(cardsKey, index, 'nraCover', 'NRA of cover')}
          {renderCurrencyCardField(cardsKey, index, 'monthlyInsurancePremium', 'Monthly insurance premium', false)}
        </div>
        <div className="flex flex-row gap-lg">
          {renderSelectCardField(cardsKey, index, 'specifiedIllnessExclusions', 'Does the policy have loading conditions', YES_NO_BENEFIT_OPTIONS)}
          {renderTextField(cardsKey, index, 'continuationOptionDetails', 'Continuation option details')}
        </div>
        <div className="flex flex-row gap-lg">
          {renderTextField(cardsKey, index, 'whoPaysThePremium', 'Who pays the premium?')}
          {renderTextField(cardsKey, index, 'indexLinked', 'Index linked?')}
        </div>

        {renderFileUploadOrSaved(`specifiedIllnessDoc_${index}`, getVal(`specifiedIllnessDoc_${index}`), sectionKey, 'Specified Illness Policy Doc')}

        <Button variant="outlined" colour="red" size="sm" type="button" onClick={() => removeCard(cardsKey, index)}>
          Remove Specified Illness
        </Button>
      </div>
    );

    // ── Default (Life Cover / Income Protection) card body ──
    const renderDefaultBody = (config: typeof BENEFIT_CONFIG[number], card: BenefitCardEntry, index: number) => {
      const isLifeCover = config.key === 'lifeCover';
      const isIncomeProtection = config.key === 'incomeProtection';
      const typeLabel = isLifeCover ? 'Type of Life Cover' : 'Type of Cover';
      const showContinuationOption = (isLifeCover && card.typeCover === 'Death in Service') || (isIncomeProtection && card.typeCover === 'Executive Income Protection');
      return (
        <div className="flex flex-col gap-md px-lg pb-[20px]">
          {/* Type of Cover | Name of Life Covered */}
          <div className="flex flex-row gap-lg">
            {renderSelectCardField(config.cardsKey, index, 'typeCover', typeLabel, config.typeOptions)}
            {renderTextField(config.cardsKey, index, 'nameCovered', 'Name of Life Covered')}
          </div>

          {/* Policy Reference Number | Salary on record */}
          <div className="flex flex-row gap-lg">
            {renderTextField(config.cardsKey, index, 'policyRefNumber', 'Policy Reference Number')}
            <div className="flex flex-col gap-sm flex-1">
              <div className={abLabelClass}>{config.salaryLabel} <span className="text-[#E7000B]">*</span></div>
              {isLifeCover ? (
                <div className={abCurrencyWrapper}>
                  <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                  <input type="text" inputMode="decimal" value={card.salaryOnRecord ? formatCurrencyDisplay(card.salaryOnRecord) : ''} onChange={(e) => handleCurrencyChange(config.cardsKey, index, 'salaryOnRecord', e.target.value)} onBlur={() => handleCurrencyBlur(config.cardsKey, index, 'salaryOnRecord')} className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none" />
                </div>
              ) : (
                <input type="text" value={card.salaryOnRecord || ''} onChange={(e) => updateCard(config.cardsKey, index, 'salaryOnRecord', e.target.value)} className={abInputClass} />
              )}
            </div>
          </div>

          {/* NRA of cover | Times salary OR % of salary */}
          <div className="flex flex-row gap-lg">
            {renderTextField(config.cardsKey, index, 'nraCover', 'NRA of cover')}
            <div className="flex flex-col gap-sm flex-1">
              <div className={abLabelClass}>{config.secondFieldLabel} <span className="text-[#E7000B]">*</span></div>
              {isLifeCover ? (
                <input type="text" value={card.timesSalary || ''} onChange={(e) => updateCard(config.cardsKey, index, 'timesSalary', e.target.value)} className={abInputClass} />
              ) : (
                <input type="text" value={card.percentOfSalary || ''} onChange={(e) => updateCard(config.cardsKey, index, 'percentOfSalary', e.target.value)} className={abInputClass} />
              )}
            </div>
          </div>

          {/* Premium from pension | Continuation option details */}
          <div className="flex flex-row gap-lg items-end">
            {renderSelectCardField(config.cardsKey, index, 'premiumFromPension', 'Is the premium being paid using units from a pension policy?', YES_NO_BENEFIT_OPTIONS)}
            {showContinuationOption ? (
              renderTextField(config.cardsKey, index, 'continuationOptionDetails', 'Continuation option details')
            ) : (
              <div className="flex-1" />
            )}
          </div>

          {/* File upload */}
          {renderFileUploadOrSaved(`${config.key}Doc_${index}`, getVal(`${config.key}Doc_${index}`), sectionKey, `${config.label} Policy Doc`)}

          {/* Remove button */}
          <Button variant="outlined" colour="red" size="sm" type="button" onClick={() => removeCard(config.cardsKey, index)}>
            Remove {config.label}
          </Button>
        </div>
      );
    };

    // ── Render cards for a benefit type ──
    const renderBenefitCards = (config: typeof BENEFIT_CONFIG[number]) => {
      const cards = getCards(config.cardsKey);
      const isSpecifiedIllness = config.key === 'specifiedIllness';

      return (
        <div className="flex flex-col gap-md">
          {cards.map((card, index) => {
            const cardId = `${config.key}_m_${index}`;
            // Collapsed state stored in fullSectionData as _collapsed_{cardId}
            const collapsedKey = `_collapsed_${cardId}`;
            const isCollapsed = !!fullSectionData[collapsedKey];

            return (
              <div key={cardId} className="flex flex-col bg-lilac-06 rounded-button overflow-hidden">
                {/* Card header */}
                <button
                  type="button"
                  onClick={() => handleFieldChange(sectionKey, collapsedKey, !isCollapsed)}
                  className="flex items-center justify-between px-lg py-md bg-transparent border-none cursor-pointer w-full"
                >
                  <div className="text-black font-sans text-[18px] font-medium leading-normal">
                    {config.label}
                  </div>
                  {abCollapseIcon(!isCollapsed)}
                </button>

                {/* Card body */}
                {!isCollapsed && (
                  isSpecifiedIllness
                    ? renderSpecifiedIllnessBody(index, config.cardsKey)
                    : renderDefaultBody(config, card, index)
                )}
              </div>
            );
          })}

          {/* Add button */}
          <Button variant="outlined" size="sm" type="button" onClick={() => addCard(config.cardsKey)}>
            Add Another {config.label}
          </Button>
        </div>
      );
    };

    // ── Main return ──
    return (
      <div className="flex flex-col gap-lg">
        {/* Gate select: hasProtectionBenefits */}
        <div className="flex flex-col gap-sm flex-1">
          <div className={abLabelClass}>
            Does the policyholder/beneficiary have any attaching protection benefits? <span className="text-[#E7000B]">*</span>
          </div>
          {renderFieldInput('otherBenefits', 'hasProtectionBenefits', fullSectionData.hasProtectionBenefits ?? '', sectionErrors[sectionKey]?.['hasProtectionBenefits'] || '')}
          <div className="text-[#1E1E1E] font-sans text-body-03 font-normal leading-normal mt-[-4px]">
            (eg. Death in service, Executive Income Protection, Income Protection, Wage Protection, Specified Illness)
          </div>
        </div>

        {hasProtection && (
          <>
            {/* Checkboxes */}
            <div className="flex flex-col gap-sm" style={{ marginTop: '8px' }}>
              <div className={abLabelClass}>
                Which of these attaching protection benefits does the policyholder/beneficiary have?
              </div>
              <div className="flex flex-row items-center gap-xxl mt-sm">
                <Checkbox
                  checked={lifeCoverEnabled}
                  onChange={(e) => handleCheckboxToggle('lifeCoverEnabled', 'lifeCoverCards', e.target.checked)}
                  label="Life Cover"
                />
                <Checkbox
                  checked={incomeProtectionEnabled}
                  onChange={(e) => handleCheckboxToggle('incomeProtectionEnabled', 'incomeProtectionCards', e.target.checked)}
                  label="Income Protection"
                />
                <Checkbox
                  checked={specifiedIllnessEnabled}
                  onChange={(e) => handleCheckboxToggle('specifiedIllnessEnabled', 'specifiedIllnessCards', e.target.checked)}
                  label="Specified Illness"
                />
              </div>
            </div>

            {/* Benefit sections */}
            {BENEFIT_CONFIG.map((config) => {
              let enabled = specifiedIllnessEnabled;
              if (config.key === 'lifeCover') enabled = lifeCoverEnabled;
              else if (config.key === 'incomeProtection') enabled = incomeProtectionEnabled;

              if (!enabled) return null;
              return (
                <div key={config.key}>
                  {renderBenefitCards(config)}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  // =========================================================================
  // Transaction History — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderTransactionHistoryContent = (
    _fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;

    return (
      <div className="flex flex-col gap-lg">
        {/* Upload 1: Transaction History */}
        {renderFileUploadOrSaved('transactionHistoryUpload', getVal('transactionHistoryUpload'), sectionKey, 'Please attach full transaction history of the account since inception')}

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Upload 2: Current Valuation */}
        {renderFileUploadOrSaved('currentValuationUpload', getVal('currentValuationUpload'), sectionKey, 'Please attached a current valuation of the account')}

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Upload 3: SORP */}
        {renderFileUploadOrSaved('sorpUpload', getVal('sorpUpload'), sectionKey, 'Please attached a Statement of Reasonable Projection (SORP)')}
      </div>
    );
  };

  // =========================================================================
  // Investment Uploads — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderInvestmentUploadsContent = (
    _fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;

    return (
      <div className="flex flex-col gap-lg">
        {renderFileUploadOrSaved('valuationUpload', getVal('valuationUpload'), sectionKey, 'Valuation (showing full breakdown and asset allocation)')}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />
        {renderFileUploadOrSaved('statementOfAccountUpload', getVal('statementOfAccountUpload'), sectionKey, 'Statement of account since inception')}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />
        {renderFileUploadOrSaved('termsOfInvestmentUpload', getVal('termsOfInvestmentUpload'), sectionKey, 'Terms of Investment Document')}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />
        {renderFileUploadOrSaved('policyDocumentUpload', getVal('policyDocumentUpload'), sectionKey, 'Policy Document')}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />
        {renderFileUploadOrSaved('mifidCostsChargesUpload', getVal('mifidCostsChargesUpload'), sectionKey, 'Mifid Costs & Charges Report')}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />
        {renderFileUploadOrSaved('mostRecentTaxPackUpload', getVal('mostRecentTaxPackUpload'), sectionKey, 'Most recent Tax Pack')}
      </div>
    );
  };

  // =========================================================================
  // Reusable File Upload Render — shows saved filename or upload button
  // =========================================================================

  const renderFileUploadOrSaved = (fieldKey: string, savedValue: unknown, sectionKey: string, label?: string) => {
    const savedFileName = typeof savedValue === 'string' && savedValue ? savedValue : null;
    const hasPending = (pendingFiles.get(fieldKey) || []).length > 0;
    const providerDoc = providerDocumentMap.get(fieldKey);

    // Priority 1: Provider-uploaded document (downloadable)
    if (providerDoc && !hasPending) {
      const docFilename = providerDoc.documentFilename || 'Document';
      const docSize = formatFileSize(providerDoc.documentSizeBytes);
      const isDownloading = downloadingDocId === providerDoc.id;
      return (
        <div className="flex flex-col gap-sm">
          {label && (
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              {label}
            </div>
          )}
          <div className="flex items-center justify-between px-lg py-md rounded-lg border border-[#A3A3A3] bg-white">
            <div className="flex items-center gap-sm">
              <AppIcon name="file document" size={32} color="#96040E" className="flex-shrink-0" />
              <div className="flex flex-col">
                <div className="text-black font-sans text-body-03 font-medium leading-normal">
                  {docFilename}
                </div>
                {docSize && (
                  <div className="text-grey-04 font-sans text-body-04 font-normal leading-normal">
                    {docSize}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleDownloadProviderDoc(providerDoc.id, docFilename)}
              disabled={isDownloading}
              className="flex items-center justify-center w-[36px] h-[36px] rounded-full hover:bg-grey-06 transition-colors cursor-pointer border-0 bg-transparent"
              title="Download document"
            >
              {isDownloading ? (
                <Spinner />
              ) : (
                <AppIcon name="download arrow" size={20} color="#525252" />
              )}
            </button>
          </div>
        </div>
      );
    }

    // Priority 2: Advisor-saved filename (read-only display)
    if (savedFileName && !hasPending) {
      return (
        <div className="flex flex-col gap-sm">
          {label && (
            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
              {label}
            </div>
          )}
          <div className="flex items-center justify-between px-lg py-md rounded-lg border border-[#A3A3A3] bg-white">
            <div className="flex items-center gap-sm">
              <AppIcon name="file document" size={32} color="#004E24" />
              <div className="text-black font-sans text-body-03 font-medium leading-normal">
                {savedFileName}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Priority 3: Upload button
    return (
      <FileUploadField
        fieldKey={fieldKey}
        label={label}
        files={pendingFiles.get(fieldKey)}
        onAddFile={(key, file) => {
          addPendingFile(key, file);
          handleFieldChange(sectionKey, fieldKey, file.name);
        }}
        onRemoveFile={(key, index) => {
          removePendingFile(key, index);
        }}
      />
    );
  };

  // Transfers IN — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderTransfersInField = (fieldName: string, value: unknown, fieldError?: string) => {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return null;
    return (
      <div className="flex flex-col gap-sm flex-1">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          {meta.label}{meta.required && <span className="text-[#E7000B]"> *</span>}
        </div>
        {renderFieldInput('transfersIn', fieldName, value, fieldError)}
      </div>
    );
  };

  const renderTransfersInContent = (
    fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';
    const hasTransfers = asScalarString(fullSectionData.hasTransferFromOtherSchemes) === 'Yes';
    const isSameEmployment = asScalarString(fullSectionData.sameEmployment) === 'Yes';
    const isSameEmploymentNo = asScalarString(fullSectionData.sameEmployment) === 'No';
    const isOverseas = asScalarString(fullSectionData.transferFromOverseas) === 'Yes';
    const isTaxPaid = asScalarString(fullSectionData.wasTaxPaid) === 'Yes';

    return (
      <div className="flex flex-col gap-lg">
        {/* Gate: Has the policy received any transfers? */}
        {renderTransfersInField('hasTransferFromOtherSchemes', getVal('hasTransferFromOtherSchemes'), getErr('hasTransferFromOtherSchemes'))}

        {hasTransfers && (
          <>
            {/* Row: Amount Transferred in | Policy/Account Provider */}
            <div className="flex flex-row gap-lg">
              {renderTransfersInField('detailAmountTransferred', getVal('detailAmountTransferred'), getErr('detailAmountTransferred'))}
              {renderTransfersInField('detailProvider', getVal('detailProvider'), getErr('detailProvider'))}
            </div>

            {/* Row: Date Transferred in | Policy/Account/Pension Type */}
            <div className="flex flex-row gap-lg">
              {renderTransfersInField('detailDateTransferred', getVal('detailDateTransferred'), getErr('detailDateTransferred'))}
              {renderTransfersInField('detailPolicyType', getVal('detailPolicyType'), getErr('detailPolicyType'))}
            </div>

            {/* Row: Reference Number | Scheme Name */}
            <div className="flex flex-row gap-lg">
              {renderTransfersInField('detailReferenceNumber', getVal('detailReferenceNumber'), getErr('detailReferenceNumber'))}
              {renderTransfersInField('detailSchemeName', getVal('detailSchemeName'), getErr('detailSchemeName'))}
            </div>

            {/* ── Divider ── */}
            <div className="w-full h-[1px] bg-grey-04" />

            {/* Same employment? */}
            {renderTransfersInField('sameEmployment', getVal('sameEmployment'), getErr('sameEmployment'))}

            {/* Section 4A: Same Employment = Yes */}
            {isSameEmployment && (
              <>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('sameEmpDateTransferred', getVal('sameEmpDateTransferred'), getErr('sameEmpDateTransferred'))}
                  {renderTransfersInField('sameEmpAmountTransferred', getVal('sameEmpAmountTransferred'), getErr('sameEmpAmountTransferred'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('employeeContributionsTransferred', getVal('employeeContributionsTransferred'), getErr('employeeContributionsTransferred'))}
                  {renderTransfersInField('employerContributionsTransferred', getVal('employerContributionsTransferred'), getErr('employerContributionsTransferred'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('transferPreviousProviderName', getVal('transferPreviousProviderName'), getErr('transferPreviousProviderName'))}
                  {renderTransfersInField('transferPreviousPolicyNumber', getVal('transferPreviousPolicyNumber'), getErr('transferPreviousPolicyNumber'))}
                </div>
                {renderTransfersInField('transferType', getVal('transferType'), getErr('transferType'))}
              </>
            )}

            {/* Section 4B: Same Employment = No */}
            {isSameEmploymentNo && (
              <>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('previousProvidersName', getVal('previousProvidersName'), getErr('previousProvidersName'))}
                  {renderTransfersInField('previousPolicyNumberNo', getVal('previousPolicyNumberNo'), getErr('previousPolicyNumberNo'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('employerDetails', getVal('employerDetails'), getErr('employerDetails'))}
                  {renderTransfersInField('transferDateJoinedService', getVal('transferDateJoinedService'), getErr('transferDateJoinedService'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('transferDateJoinedScheme', getVal('transferDateJoinedScheme'), getErr('transferDateJoinedScheme'))}
                  {renderTransfersInField('transferDateOfLeavingService', getVal('transferDateOfLeavingService'), getErr('transferDateOfLeavingService'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('salaryAtLeavingService', getVal('salaryAtLeavingService'), getErr('salaryAtLeavingService'))}
                  {renderTransfersInField('totalEmployerContributions', getVal('totalEmployerContributions'), getErr('totalEmployerContributions'))}
                </div>
                <div className="flex flex-row gap-lg">
                  {renderTransfersInField('totalEmployeeContributions', getVal('totalEmployeeContributions'), getErr('totalEmployeeContributions'))}
                  {renderTransfersInField('transferFromOverseas', getVal('transferFromOverseas'), getErr('transferFromOverseas'))}
                </div>

                {/* Overseas fields */}
                {isOverseas && (
                  <>
                    <div className="flex flex-row gap-lg">
                      {renderTransfersInField('overseasCountry', getVal('overseasCountry'), getErr('overseasCountry'))}
                      {renderTransfersInField('wasTaxPaid', getVal('wasTaxPaid'), getErr('wasTaxPaid'))}
                    </div>

                    {/* Tax details */}
                    {isTaxPaid && (
                      <div className="flex flex-col gap-sm">
                        <TextArea
                          label="Please provide details and backup."
                          required
                          placeholder="Overseas tax paid details"
                          maxLength={2000}
                          value={asScalarString(getVal('overseasTaxDetails'))}
                          onChange={(e) => handleFieldChange(sectionKey, 'overseasTaxDetails', e.target.value)}
                          fluid
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Upload Documents (always when hasTransfers) */}
            {renderFileUploadOrSaved('transferTaxBackupUpload', getVal('transferTaxBackupUpload'), sectionKey)}
          </>
        )}
      </div>
    );
  };

  // =========================================================================
  // Additional Information — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderAdditionalInfoField = (fieldName: string, value: unknown, fieldError?: string) => {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return null;
    return (
      <div className="flex flex-col gap-sm flex-1">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          {meta.label}{meta.required && <span className="text-[#E7000B]"> *</span>}
        </div>
        {renderFieldInput('additionalInfo', fieldName, value, fieldError)}
      </div>
    );
  };

  const renderAdditionalInfoContent = (
    fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';
    const isPolicyAssigned = asScalarString(fullSectionData.hasPolicyBeenAssigned) === 'Yes';
    const isPensionAdj = asScalarString(fullSectionData.hasPensionAdjustmentOrder) === 'Yes';
    const isDeathBenefit = asScalarString(fullSectionData.hasUniqueDeathBenefit) === 'Yes';

    return (
      <div className="flex flex-col gap-lg">
        {/* ── Policy Assignment ── */}
        {renderAdditionalInfoField('hasPolicyBeenAssigned', getVal('hasPolicyBeenAssigned'), getErr('hasPolicyBeenAssigned'))}

        {isPolicyAssigned && (
          <>
            {/* Row: To whom | Date — 2 cols */}
            <div className="flex flex-row gap-lg">
              {renderAdditionalInfoField('policyAssignedTo', getVal('policyAssignedTo'), getErr('policyAssignedTo'))}
              {renderAdditionalInfoField('policyAssignedDate', getVal('policyAssignedDate'), getErr('policyAssignedDate'))}
            </div>

            {/* Other Details textarea */}
            <div className="flex flex-col gap-sm">
              <TextArea
                label="Other Details"
                placeholder="Other Details"
                maxLength={2000}
                value={asScalarString(getVal('policyAssignedDetails'))}
                onChange={(e) => handleFieldChange(sectionKey, 'policyAssignedDetails', e.target.value)}
                fluid
              />
            </div>

            {/* Upload Documents */}
            {renderFileUploadOrSaved('assignedDocUpload', getVal('assignedDocUpload'), sectionKey)}
          </>
        )}

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* ── Pension Adjustment Order ── */}
        {renderAdditionalInfoField('hasPensionAdjustmentOrder', getVal('hasPensionAdjustmentOrder'), getErr('hasPensionAdjustmentOrder'))}

        {isPensionAdj && (
          <>
            {renderAdditionalInfoField('pensionAdjustmentDate', getVal('pensionAdjustmentDate'), getErr('pensionAdjustmentDate'))}

            {/* Other Details textarea */}
            <div className="flex flex-col gap-sm">
              <TextArea
                label="Other Details"
                placeholder="Other Details"
                maxLength={2000}
                value={asScalarString(getVal('pensionAdjustmentDetails'))}
                onChange={(e) => handleFieldChange(sectionKey, 'pensionAdjustmentDetails', e.target.value)}
                fluid
              />
            </div>

            {/* Upload PAO details */}
            {renderFileUploadOrSaved('pensionAdjustmentOrderDocument', getVal('pensionAdjustmentOrderDocument'), sectionKey, 'Upload PAO details')}
          </>
        )}

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* ── Unique Death Benefit ── */}
        {renderAdditionalInfoField('hasUniqueDeathBenefit', getVal('hasUniqueDeathBenefit'), getErr('hasUniqueDeathBenefit'))}

        {isDeathBenefit && (
          <>
            {renderAdditionalInfoField('uniqueDeathBenefitExpiry', getVal('uniqueDeathBenefitExpiry'), getErr('uniqueDeathBenefitExpiry'))}

            {/* Please provide details textarea */}
            <div className="flex flex-col gap-sm">
              <TextArea
                label="Please provide details"
                placeholder="Please provide details"
                maxLength={2000}
                value={asScalarString(getVal('uniqueDeathBenefitDetails'))}
                onChange={(e) => handleFieldChange(sectionKey, 'uniqueDeathBenefitDetails', e.target.value)}
                fluid
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // =========================================================================
  // Investment Other Information — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderInvestmentOtherInfoContent = (
    _fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';

    const renderField = (fieldName: string, value: unknown, fieldError?: string) => {
      const meta = FIELD_METADATA[fieldName];
      if (!meta) return null;
      return (
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            {meta.label}{meta.required && <span className="text-[#E7000B]"> *</span>}
          </div>
          {renderFieldInput(sectionKey, fieldName, value, fieldError)}
        </div>
      );
    };

    return (
      <div className="flex flex-col gap-lg">
        {/* Row 1: Has policy/account been assigned or pledged? | To whom? | What date? */}
        <div className="flex flex-row gap-lg">
          {renderField('policyAssigned', getVal('policyAssigned'), getErr('policyAssigned'))}
          {renderField('policyAssignedTo', getVal('policyAssignedTo'), getErr('policyAssignedTo'))}
          {renderField('policyAssignedDate', getVal('policyAssignedDate'), getErr('policyAssignedDate'))}
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Row 2: Has policy a bare trust or deed of assignment? | To whom? */}
        <div className="flex flex-row gap-lg">
          {renderField('hasBareTrustOrDeed', getVal('hasBareTrustOrDeed'), getErr('hasBareTrustOrDeed'))}
          {renderField('bareTrustOrDeedTo', getVal('bareTrustOrDeedTo'), getErr('bareTrustOrDeedTo'))}
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* Row 3: Is there a unique death benefit? */}
        {renderField('uniqueDeathBenefit', getVal('uniqueDeathBenefit'), getErr('uniqueDeathBenefit'))}
      </div>
    );
  };

  // =========================================================================
  // Charges & Fees — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderChargesFeesField = (fieldName: string, value: unknown, fieldError?: string) => {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return null;
    return (
      <div className="flex flex-col gap-sm flex-1">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          {meta.label}{meta.required && <span className="text-[#E7000B]"> *</span>}
        </div>
        {renderFieldInput('chargesFees', fieldName, value, fieldError)}
      </div>
    );
  };

  const renderChargesFeesContent = (
    _fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';

    return (
      <div className="flex flex-col gap-lg">
        {/* Row 1: Fund Management Charge | Commissions */}
        <div className="flex flex-row gap-lg">
          {renderChargesFeesField('brokerAdvisoryAmc', getVal('brokerAdvisoryAmc'), getErr('brokerAdvisoryAmc'))}
          {renderChargesFeesField('commissionsOnPolicy', getVal('commissionsOnPolicy'), getErr('commissionsOnPolicy'))}
        </div>

        {/* Row 2: Allocation % | Exit Charges */}
        <div className="flex flex-row gap-lg">
          {renderChargesFeesField('allocationPercentContributions', getVal('allocationPercentContributions'), getErr('allocationPercentContributions'))}
          {renderChargesFeesField('exitChargesPenalties', getVal('exitChargesPenalties'), getErr('exitChargesPenalties'))}
        </div>

        {/* Row 3: Transfer out costs (half-width) */}
        <div className="flex flex-row gap-lg">
          {renderChargesFeesField('transferOutCosts', getVal('transferOutCosts'), getErr('transferOutCosts'))}
          <div className="flex-1" />
        </div>

        {/* ── Divider ── */}
        <div className="w-full h-[1px] bg-[#E1E1E1]" />

        {/* ── Other Charges & Details ── */}
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
          Other Charges &amp; Details
        </div>

        {/* Row 4: Guaranteed Returns | Terminal Bonus */}
        <div className="flex flex-row gap-lg">
          {renderChargesFeesField('guaranteedReturns', getVal('guaranteedReturns'), getErr('guaranteedReturns'))}
          {renderChargesFeesField('terminalBonusDetails', getVal('terminalBonusDetails'), getErr('terminalBonusDetails'))}
        </div>

        {/* Other Charge Details (textarea full-width) */}
        <div className="flex flex-col gap-sm">
          <TextArea
            label="Other Charge Details"
            required
            placeholder="Other Charge Details"
            maxLength={2000}
            value={asScalarString(getVal('otherChargeDetails'))}
            onChange={(e) => handleFieldChange(sectionKey, 'otherChargeDetails', e.target.value)}
            fluid
          />
        </div>

        {/* Upload Documents */}
        {renderFileUploadOrSaved('chargesFeesDocUpload', getVal('chargesFeesDocUpload'), sectionKey)}
      </div>
    );
  };

  // =========================================================================
  // Investments — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderInvestmentsContent = (
    fullSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    interface FundEntry { fundName?: string; allocationPercent?: string; futureContributionPercent?: string; riskRating?: string; annualManagementCharge?: string; totalExpenseRatio?: string; performanceFees?: string; bidOfferSpread?: string; marketValueAdjustment?: string }
    const funds = (fullSectionData.investmentFunds as FundEntry[] | undefined) || [];

    const toRawPercent = (display: string): string => {
      const stripped = display.replaceAll(/[^0-9.]/g, '');
      const parts = stripped.split('.');
      if (parts.length <= 1) return stripped;
      return parts[0] + '.' + parts.slice(1).join('');
    };

    const updateFund = (index: number, field: keyof FundEntry, value: string) => {
      const updated = [...funds];
      updated[index] = { ...updated[index], [field]: value };
      handleFieldChange(sectionKey, 'investmentFunds', updated);
    };

    const addFund = () => {
      handleFieldChange(sectionKey, 'investmentFunds', [...funds, {}]);
    };

    const removeFund = (index: number) => {
      handleFieldChange(sectionKey, 'investmentFunds', funds.filter((_, i) => i !== index));
    };

    const handlePercentChange = (index: number, field: keyof FundEntry, inputValue: string) => {
      const raw = toRawPercent(inputValue);
      if (raw && !/^\d*\.?\d*$/.test(raw)) return;
      updateFund(index, field, raw);
    };

    const inputClass = "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";
    const percentWrapper = "flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary";

    const trashSvg = (
      <AppIcon name="trash field" size={16} />
    );

    const addSvg = (
      <AppIcon name="plus field" size={16} />
    );

    const percentInput = (val: string, onChange: (v: string) => void) => (
      <div className={percentWrapper}>
        <input
          type="text"
          inputMode="decimal"
          value={val || ''}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
        />
        <span className="text-grey-04 font-sans text-body-02 font-normal leading-normal select-none ml-sm">%</span>
      </div>
    );

    return (
      <div className="flex flex-col gap-lg">
        {funds.map((fund, index) => (
          <div key={index} className="flex flex-col gap-md bg-lilac-06 rounded-button px-lg py-[20px]">
            {/* Row 1: Fund name | Weighting % Allocation */}
            <div className="flex flex-row gap-lg">
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Investment fund name <span className="text-[#E7000B]">*</span>
                </div>
                <input
                  type="text"
                  value={fund.fundName || ''}
                  onChange={(e) => updateFund(index, 'fundName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Weighting % Allocation of Investment Funds <span className="text-[#E7000B]">*</span>
                </div>
                {percentInput(fund.allocationPercent || '', (v) => handlePercentChange(index, 'allocationPercent', v))}
              </div>
            </div>

            {/* Row 2: Future contribution % | Risk Rating */}
            <div className="flex flex-row gap-lg items-end">
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Weighting % Allocation of Investment Funds for future contributions
                </div>
                {percentInput(fund.futureContributionPercent || '', (v) => handlePercentChange(index, 'futureContributionPercent', v))}
              </div>
              <div className="flex flex-col gap-sm flex-1">
                <Dropdown
                  label="Risk Rating of investment fund"
                  required
                  placeholder="Choose an option"
                  options={[1,2,3,4,5,6,7].map(n => ({ value: String(n), text: String(n) }))}
                  value={fund.riskRating || ''}
                  onChange={(value) => updateFund(index, 'riskRating', value)}
                />
              </div>
            </div>

            {/* Row 3: Annual Management Charge | Total Expense Ratio */}
            <div className="flex flex-row gap-lg">
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Annual Management Charge <span className="text-[#E7000B]">*</span>
                </div>
                {percentInput(fund.annualManagementCharge || '', (v) => handlePercentChange(index, 'annualManagementCharge', v))}
              </div>
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Total Expense Ratio (TER) <span className="text-[#E7000B]">*</span>
                </div>
                {percentInput(fund.totalExpenseRatio || '', (v) => handlePercentChange(index, 'totalExpenseRatio', v))}
              </div>
            </div>

            {/* Row 4: Performance Fees | Bid/Offer Spread */}
            <div className="flex flex-row gap-lg">
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Performance Fees <span className="text-[#E7000B]">*</span>
                </div>
                {percentInput(fund.performanceFees || '', (v) => handlePercentChange(index, 'performanceFees', v))}
              </div>
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Bid/Offer Spread <span className="text-[#E7000B]">*</span>
                </div>
                {percentInput(fund.bidOfferSpread || '', (v) => handlePercentChange(index, 'bidOfferSpread', v))}
              </div>
            </div>

            {/* Row 5: Market Value Adjustment */}
            <div className="flex flex-row gap-lg">
              <div className="flex flex-col gap-sm flex-1">
                <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                  Market Value Adjustment (MVA) / With Profits <span className="text-[#E7000B]">*</span>
                </div>
                <input
                  type="text"
                  value={fund.marketValueAdjustment || ''}
                  onChange={(e) => updateFund(index, 'marketValueAdjustment', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="flex-1" />
            </div>

            {/* Remove fund button */}
            <button
              type="button"
              onClick={() => removeFund(index)}
              className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
            >
              {trashSvg}
              <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
                Remove this fund
              </div>
            </button>
          </div>
        ))}

        {/* Add fund button */}
        <button
          type="button"
          onClick={addFund}
          className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
        >
          {addSvg}
          <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
            Add Another Fund
          </div>
        </button>
      </div>
    );
  };

  // =========================================================================
  // Crystallised / Retired — Custom Section Render (mirrors wizard exactly)
  // =========================================================================

  const renderCrystallisedField = (fieldName: string, value: unknown, fieldError?: string) => {
    const meta = FIELD_METADATA[fieldName];
    if (!meta) return null;
    return (
      <div className="flex flex-col gap-sm flex-1">
        <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
          {meta.label}{meta.required && <span className="text-[#E7000B]"> *</span>}
        </div>
        {renderFieldInput('crystallisedRetired', fieldName, value, fieldError)}
      </div>
    );
  };

  const renderCrystallisedRetiredContent = (
    fullSectionData: Record<string, unknown>,
    currentSectionData: Record<string, unknown>,
    sectionKey: string
  ) => {
    const getVal = (f: string) => f in currentSectionData ? currentSectionData[f] : mergedFields[f]?.value;
    const getErr = (f: string) => sectionErrors[sectionKey]?.[f] || '';
    const isCrystallised = asScalarString(fullSectionData.hasBeenCrystallised) === 'Yes';
    const isDrawing = asScalarString(fullSectionData.isDrawingIncome) === 'Yes';

    // Additional lump sums array
    interface LumpSumEntry { amount?: string; taxPaid?: string; date?: string }
    const additionalLumpSums = (fullSectionData.additionalLumpSums as LumpSumEntry[] | undefined) || [];

    const updateLumpSumEntry = (index: number, field: keyof LumpSumEntry, value: string) => {
      const updated = [...additionalLumpSums];
      updated[index] = { ...updated[index], [field]: value };
      handleFieldChange(sectionKey, 'additionalLumpSums', updated);
    };

    const addLumpSumEntry = () => {
      handleFieldChange(sectionKey, 'additionalLumpSums', [...additionalLumpSums, { amount: '', taxPaid: '', date: '' }]);
    };

    const removeLumpSumEntry = (index: number) => {
      handleFieldChange(sectionKey, 'additionalLumpSums', additionalLumpSums.filter((_, i) => i !== index));
    };

    const calendarIcon = (
      <AppIcon
        name="calendar field"
        size={16}
        color="#141414"
        className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none"
      />
    );

    const dateInputClass = (hasValue: boolean) =>
      `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

    const currencyInput = (_fieldName: string, val: string, onChange: (v: string) => void, onBlur: () => void) => (
      <div className="flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary">
        <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
        <input
          type="text"
          inputMode="decimal"
          value={val ? formatCurrencyDisplay(val) : ''}
          onChange={(e) => {
            const raw = toRawNumber(e.target.value);
            if (raw && !/^\d*\.?\d*$/.test(raw)) return;
            onChange(raw);
          }}
          onBlur={onBlur}
          className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
        />
      </div>
    );

    const trashIcon = (
      <AppIcon name="trash field" size={16} />
    );

    const addIcon = (
      <AppIcon name="plus field" size={16} />
    );

    return (
      <div className="flex flex-col gap-lg">
        {/* Has the policy been crystallised/retired? */}
        {renderCrystallisedField('hasBeenCrystallised', getVal('hasBeenCrystallised'), getErr('hasBeenCrystallised'))}

        {isCrystallised && (
          <>
            {/* Row: Date of BCE | Value of Policy as at BCE */}
            <div className="flex flex-row gap-lg">
              {renderCrystallisedField('crystallisedDate', getVal('crystallisedDate'), getErr('crystallisedDate'))}
              {renderCrystallisedField('crystallisedAmount', getVal('crystallisedAmount'), getErr('crystallisedAmount'))}
            </div>

            {/* Row: Provider name | QFM name */}
            <div className="flex flex-row gap-lg">
              {renderCrystallisedField('crystallisedProviderName', getVal('crystallisedProviderName'), getErr('crystallisedProviderName'))}
              {renderCrystallisedField('crystallisedQfmName', getVal('crystallisedQfmName'), getErr('crystallisedQfmName'))}
            </div>

            {/* ── Divider ── */}
            <div className="w-full h-[1px] bg-[#E1E1E1]" />

            {/* ── Lump Sums ── */}
            <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
              Lump Sums
            </div>

            {/* Row: Tax Free Lump Sum | Date of Tax Free */}
            <div className="flex flex-row gap-lg">
              {renderCrystallisedField('taxFreeLumpSum', getVal('taxFreeLumpSum'), getErr('taxFreeLumpSum'))}
              {renderCrystallisedField('taxFreeLumpSumDate', getVal('taxFreeLumpSumDate'), getErr('taxFreeLumpSumDate'))}
            </div>

            {/* Row: Other Lump Sum | Tax Paid On Other | Date of Lump Sum — 3 cols */}
            <div className="flex flex-row gap-lg">
              {renderCrystallisedField('otherLumpSum', getVal('otherLumpSum'), getErr('otherLumpSum'))}
              {renderCrystallisedField('taxPaidOtherLumpSum', getVal('taxPaidOtherLumpSum'), getErr('taxPaidOtherLumpSum'))}
              {renderCrystallisedField('otherLumpSumDate', getVal('otherLumpSumDate'), getErr('otherLumpSumDate'))}
            </div>

            {/* Additional Lump Sum entries */}
            {additionalLumpSums.map((entry, index) => (
              <div key={index} className="flex flex-col gap-md rounded-lg p-md" style={{ background: '#F8F8FF' }}>
                <div className="flex flex-row gap-lg">
                  <div className="flex flex-col gap-sm flex-1">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Other Lump Sum <span className="text-[#E7000B]">*</span>
                    </div>
                    {currencyInput(`additionalLumpSum_amount_${index}`, entry.amount || '', (v) => updateLumpSumEntry(index, 'amount', v), () => { if (entry.amount) updateLumpSumEntry(index, 'amount', formatEuroBlur(entry.amount)); })}
                  </div>
                  <div className="flex flex-col gap-sm flex-1">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Tax Paid On Other Lump Sum <span className="text-[#E7000B]">*</span>
                    </div>
                    {currencyInput(`additionalLumpSum_taxPaid_${index}`, entry.taxPaid || '', (v) => updateLumpSumEntry(index, 'taxPaid', v), () => { if (entry.taxPaid) updateLumpSumEntry(index, 'taxPaid', formatEuroBlur(entry.taxPaid)); })}
                  </div>
                  <div className="flex flex-col gap-sm flex-1">
                    <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                      Date of Lump Sum <span className="text-[#E7000B]">*</span>
                    </div>
                    <div className="relative">
                      <input
                        type={entry.date ? 'date' : 'text'}
                        placeholder="DD/MM/YYYY"
                        value={entry.date || ''}
                        onFocus={(e) => { e.currentTarget.type = 'date'; }}
                        onBlur={(e) => { if (!e.currentTarget.value) e.currentTarget.type = 'text'; }}
                        onChange={(e) => updateLumpSumEntry(index, 'date', e.target.value)}
                        className={dateInputClass(!!entry.date)}
                      />
                      {calendarIcon}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeLumpSumEntry(index)}
                  className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
                >
                  {trashIcon}
                  <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
                    Remove This Lump Sum
                  </div>
                </button>
              </div>
            ))}

            {/* Add Another Lump Sum button */}
            <button
              type="button"
              onClick={addLumpSumEntry}
              className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
            >
              {addIcon}
              <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
                Add Another Lump Sum
              </div>
            </button>

            {/* Upload Documents */}
            {renderFileUploadOrSaved('crystallisedDocumentUpload', getVal('crystallisedDocumentUpload'), sectionKey)}

            {/* ── Divider ── */}
            <div className="w-full h-[1px] bg-[#E1E1E1]" />

            {/* ── Income drawn from policy ── */}
            <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
              Income drawn from policy
            </div>

            {/* Is the Policyholder drawing income from the policy? */}
            {renderCrystallisedField('isDrawingIncome', getVal('isDrawingIncome'), getErr('isDrawingIncome'))}

            {isDrawing && (
              <>
                {/* Row: Annual Drawdown % | Last Annual Drawdown */}
                <div className="flex flex-row gap-lg">
                  {renderCrystallisedField('annualDrawdownPercent', getVal('annualDrawdownPercent'), getErr('annualDrawdownPercent'))}
                  {renderCrystallisedField('lastAnnualDrawdown', getVal('lastAnnualDrawdown'), getErr('lastAnnualDrawdown'))}
                </div>

                {/* Row: Drawdown Payment Frequency | Date of Drawdown Payment */}
                <div className="flex flex-row gap-lg">
                  {renderCrystallisedField('drawdownFrequency', getVal('drawdownFrequency'), getErr('drawdownFrequency'))}
                  {renderCrystallisedField('drawdownPaymentDate', getVal('drawdownPaymentDate'), getErr('drawdownPaymentDate'))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  };

  // =========================================================================
  // Main Render
  // =========================================================================

  if (loading && !structuredDetails) {
    return (
      <div className="p-0 min-h-[200px]">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-0">
      {error && (
        <Alert variant="error" header="Error" dismissible onDismiss={clearError}>
          <p>{error}</p>
        </Alert>
      )}

      <div className="flex flex-col gap-lg">
        {METADATA_ACCORDION_SECTIONS.filter(s => !hiddenSections.has(s.key)).map((section) => {
          const isExpanded = expandedSections.has(section.key);
          const isDirty = dirtySections.has(section.key);
          const currentSectionData = sectionFormData[section.key] || {};

          // Build full section data: merged values + user edits
          const fullSectionData: Record<string, unknown> = {};
          for (const f of section.fields) {
            if (f in currentSectionData) {
              fullSectionData[f] = currentSectionData[f];
            } else {
              const merged = mergedFields[f];
              if (merged?.value !== undefined && merged.value !== null) {
                fullSectionData[f] = merged.value;
              }
            }
          }
          // Country always has a default value (dropdown never empty)
          if (section.fields.includes('customerCountry') && !fullSectionData.customerCountry) {
            fullSectionData.customerCountry = DEFAULT_COUNTRY;
          }
          // typeOfPolicy defaults from policyCategory prop
          if (section.fields.includes('typeOfPolicy') && !fullSectionData.typeOfPolicy && policyCategory) {
            fullSectionData.typeOfPolicy = policyCategory.toUpperCase();
          }

          // Check if section has any data
          const hasData = section.fields.some(f => {
            const merged = mergedFields[f];
            return merged?.value !== undefined && merged.value !== null && merged.value !== '';
          });

          return (
            <div
              key={section.key}
              className={`metadata-section bg-white rounded-card shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] overflow-hidden ${
                isExpanded ? 'px-md py-lg flex flex-col gap-xl' : ''
              }`}
            >
              <button
                type="button"
                className={`flex items-center justify-between w-full cursor-pointer text-left bg-transparent border-0 ${
                  isExpanded
                    ? 'p-0'
                    : 'px-md py-lg'
                }`}
                onClick={() => toggleSection(section.key)}
              >
                <div className="font-sans text-h5 font-normal text-black leading-normal">
                  {section.label}
                </div>
                <div className="flex items-center gap-lg ml-auto">
                  {hasData && (
                    <AppIcon name="status dot" size={16} color="#00A63E" className="flex-shrink-0" />
                  )}
                  {isExpanded ? (
                    <AppIcon name="chevron compact up" size={18} color="#141414" className="flex-shrink-0" />
                  ) : (
                    <AppIcon name="chevron compact down" size={24} color="#141414" className="flex-shrink-0" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div>
                  {/* Custom render for sections that mirror wizard layout */}
                  {WIZARD_LAYOUT_SECTION_KEYS.has(section.key) ? (
                    <>
                      {(() => {
                        const customSectionRenderers: Record<string, () => React.ReactNode> = {
                          investmentUploads: () => renderInvestmentUploadsContent(fullSectionData, currentSectionData, section.key),
                          investmentOtherInfo: () => renderInvestmentOtherInfoContent(fullSectionData, currentSectionData, section.key),
                          policyDetails: () => renderPolicyDetailsContent(fullSectionData, currentSectionData, section.key),
                          crystallisedRetired: () => renderCrystallisedRetiredContent(fullSectionData, currentSectionData, section.key),
                          investments: () => renderInvestmentsContent(fullSectionData, section.key),
                          investmentFunds: () => renderInvestmentsContent(fullSectionData, section.key),
                          chargesFees: () => renderChargesFeesContent(fullSectionData, currentSectionData, section.key),
                          transfersIn: () => renderTransfersInContent(fullSectionData, currentSectionData, section.key),
                          transactionHistory: () => renderTransactionHistoryContent(fullSectionData, currentSectionData, section.key),
                          otherBenefits: () => renderAssociatedBenefitsContent(fullSectionData, currentSectionData, section.key),
                        };
                        const render = customSectionRenderers[section.key];
                        return render ? render() : renderAdditionalInfoContent(fullSectionData, currentSectionData, section.key);
                      })()}

                      <div className="flex justify-end mt-xl">
                        {(() => {
                          const visibleFields = section.fields.filter(f => isFieldVisible(f, fullSectionData));
                          const sectionComplete = isSectionComplete(fullSectionData, visibleFields);
                          const canSave = isDirty && sectionComplete;
                          return (
                            <Button
                              variant="filled"
                              className={`metadata-save-btn ${canSave ? 'metadata-save-btn--active' : ''}`}
                              loading={saving && isDirty}
                              onClick={() => {
                                if (canSave) {
                                  openChangeReasonModal(section.key);
                                } else {
                                  validateAllSectionFields(section.key, visibleFields, fullSectionData);
                                }
                              }}
                            >
                              Save changes
                            </Button>
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                  <>
                  <div className="grid grid-cols-2 gap-lg max-md:grid-cols-1">
                    {section.fields.map(fieldName => {
                      const metadata = FIELD_METADATA[fieldName];
                      if (!metadata) return null;

                      // Check visibleWhen condition
                      if (!isFieldVisible(fieldName, fullSectionData)) return null;

                      // Hide contribution detail fields when Paid Up / Deferred
                      if (!isContributionFieldVisible(section.key, fieldName)) return null;

                      // Use section form data if dirty, otherwise merged value
                      const value = fieldName in currentSectionData
                        ? currentSectionData[fieldName]
                        : mergedFields[fieldName]?.value;

                      const fieldError = sectionErrors[section.key]?.[fieldName] || '';

                      // Dynamic labels and placeholders based on selected country
                      let displayLabel = metadata.label;
                      let dynamicPlaceholder: string | undefined;
                      if (fieldName === 'customerPostalCode' || fieldName === 'customerCity') {
                        const cc = resolveCountryCodeFromSection(section.key);
                        const config = getCountryConfig(cc);
                        if (fieldName === 'customerPostalCode') {
                          displayLabel = config.postalCodeLabel;
                          dynamicPlaceholder = config.postalCodePlaceholder;
                        } else if (fieldName === 'customerCity') {
                          displayLabel = config.townCityLabel;
                          dynamicPlaceholder = config.townCityPlaceholder;
                        }
                      }

                      // File type fields render full-width with their own label via FileUploadField
                      if (metadata.type === 'file') {
                        return (
                          <div key={fieldName} className="col-span-2 max-md:col-span-1">
                            {renderFieldInput(section.key, fieldName, value, fieldError || undefined)}
                          </div>
                        );
                      }

                      // Textarea fields render full-width (matching wizard layout)
                      if (metadata.type === 'textarea') {
                        return (
                          <div key={fieldName} className="col-span-2 max-md:col-span-1 flex flex-col gap-sm">
                            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                              {displayLabel}{metadata.required && <span className="text-[#E7000B]"> *</span>}
                            </div>
                            {renderFieldInput(section.key, fieldName, value, undefined, dynamicPlaceholder)}
                            {metadata.helpText && (
                              <small className="block text-grey-02 text-body-04">
                                {metadata.helpText}
                              </small>
                            )}
                          </div>
                        );
                      }

                      // Fields that sit side-by-side with uneven label heights need
                      // min-h-[20px] error placeholder to prevent layout shift (matching wizard pattern)
                      const FIELDS_WITH_ERROR_PLACEHOLDER = new Set([
                        'severancePackageReceived', 'severanceWaivedLumpSum'
                      ]);
                      const needsErrorPlaceholder = FIELDS_WITH_ERROR_PLACEHOLDER.has(fieldName);

                      return (
                        <div key={fieldName} className="flex flex-col gap-sm">
                          <div className="flex flex-col gap-sm flex-1 justify-end">
                            <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
                              {displayLabel}{metadata.required && <span className="text-[#E7000B]"> *</span>}
                            </div>
                            {renderFieldInput(section.key, fieldName, value, needsErrorPlaceholder ? undefined : (fieldError || undefined), dynamicPlaceholder)}
                          </div>
                          {needsErrorPlaceholder && (
                            <div className="min-h-[20px]">
                              {fieldError && (
                                <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{fieldError}</div>
                              )}
                            </div>
                          )}
                          {metadata.helpText && (
                            <small className="block text-grey-02 text-body-04">
                              {metadata.helpText}
                            </small>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-xl">
                    {(() => {
                      // Only validate visible fields for section completeness
                      const visibleFields = section.fields.filter(f => isFieldVisible(f, fullSectionData));
                      const sectionComplete = isSectionComplete(fullSectionData, visibleFields);
                      const canSave = isDirty && sectionComplete;

                      return (
                        <Button
                          variant="filled"
                          className={`metadata-save-btn ${canSave ? 'metadata-save-btn--active' : ''}`}
                          loading={saving && isDirty}
                          onClick={() => {
                            if (canSave) {
                              openChangeReasonModal(section.key);
                            } else {
                              validateAllSectionFields(section.key, visibleFields, fullSectionData);
                            }
                          }}
                        >
                          Save changes
                        </Button>
                      );
                    })()}
                  </div>
                  </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <PolicyMetadataChangeReasonModal
        open={changeReasonModal.open}
        onClose={() => setChangeReasonModal({ open: false, sectionKey: '', sectionLabel: '' })}
        onConfirm={handleSaveSectionWithReason}
        sectionLabel={changeReasonModal.sectionLabel}
        policyNumber={policyNumber}
      />
    </div>
  );
};

export default PolicyMetadataTab;
